
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Tooltip } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import { StudentKpi } from "@/services/studentKpiService";
import { TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Users, DollarSign, PercentSquare, Info } from "lucide-react";

interface StudentKpiCardsProps {
  kpis: StudentKpi | undefined;
  isLoading: boolean;
  periodDays: number;
}

export function StudentKpiCards({ kpis, isLoading, periodDays }: StudentKpiCardsProps) {
  const safeKpis: StudentKpi = kpis || {
    totalStudents: 0,
    newEnrollments: 0,
    activeStudents: 0,
    averageProgress: 0,
    totalRevenue: 0,
    arps: 0
  };
  
  return (
    <div className={cn(
      "grid gap-4 md:gap-4",
      "grid-cols-1 sm:grid-cols-2 md:grid-cols-4 w-full"
    )}>
      {/* Total Students */}
      <KpiCard
        title="Total Students"
        value={safeKpis.totalStudents}
        icon={<Users className="h-5 w-5 mr-2 text-[#8E9196]" />}
        tooltipContent="Total number of students enrolled across all courses"
        isLoading={isLoading}
      />

      {/* New Enrollments */}
      <KpiCard
        title="New Enrollments"
        value={safeKpis.newEnrollments}
        icon={<Users className="h-5 w-5 mr-2 text-[#87B5F5]" />}
        tooltipContent={`New students enrolled in the last ${periodDays} days`}
        isLoading={isLoading}
      />

      {/* Average Progress */}
      <KpiCard
        title="Avg. Progress"
        value={safeKpis.averageProgress}
        formatValue={(value) => `${value.toFixed(1)}%`}
        icon={<PercentSquare className="h-5 w-5 mr-2 text-[#9b87f5]" />}
        tooltipContent="Average course completion percentage across all students"
        isLoading={isLoading}
      />

      {/* Active Students */}
      <KpiCard
        title="Active Students"
        value={safeKpis.activeStudents}
        icon={<Users className="h-5 w-5 mr-2 text-[#6E59A5]" />}
        tooltipContent="Students active in the last 7 days"
        isLoading={isLoading}
      />
    </div>
  );
}

interface KpiCardProps {
  title: string;
  value: number | undefined;
  formatValue?: (value: number) => string;
  icon?: React.ReactNode;
  tooltipContent: string;
  caption?: string;
  isLoading: boolean;
}

function KpiCard({
  title,
  value,
  formatValue = (value) => value.toLocaleString(),
  icon,
  tooltipContent,
  caption,
  isLoading,
}: KpiCardProps) {
  // Safe value handling
  const safeValue = typeof value === 'number' && !isNaN(value) ? value : 0;
  
  return (
    <Card
      className={cn(
        "flex flex-col justify-between min-h-[110px] px-6 py-4 bg-background border border-muted rounded-2xl shadow-sm transition-shadow hover:shadow-md focus-within:shadow-lg"
      )}
    >
      <CardHeader className="flex flex-row items-center justify-between pb-1 px-0 pt-0 bg-transparent space-y-0">
        <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-1">
          {icon}
          <span>{title}</span>
        </CardTitle>
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Info className="h-4 w-4 text-muted-foreground/70" />
            </TooltipTrigger>
            <TooltipContent>
              <p>{tooltipContent}</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </CardHeader>
      <CardContent className="pt-0 pb-0 px-0 flex flex-col">
        {isLoading ? (
          <Skeleton className="h-8 w-20 rounded-md" />
        ) : (
          <div>
            <div className="text-2xl font-bold tracking-tight tabular-nums leading-relaxed text-dark">
              {formatValue(safeValue)}
            </div>
            {caption && (
              <p className="text-xs text-muted-foreground mt-1">{caption}</p>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
